/* Event map — events.md §EM.9.

   Does only what CSS cannot: resolving the "Past events" jump link,
   client-side search, the result counter, the Leaflet map, pin placement +
   row/pin sync, and map zoom.

   Everything else is CSS: the expanded detail card is a sibling rule on a
   hidden radio, the Details disclosure is <details>, and the share button
   is already handled by the delegated listener in assets/events.js.

   With this script absent the list still renders with its first row
   expanded; `event-map:not(:defined)` hides the search form and zoom
   control, which would otherwise do nothing. The map box stays empty —
   there is no server-rendered fallback for a tiled map. */

/* Geographic centre of each state in the block schema's list, used when a
   block has no lat/lng. Keeps the `state` setting alone enough to place a
   pin, which is how every event was placed before coordinates existed. */
/* Used until the Map theme settings are saved for the first time — an
   unsaved setting renders as an empty attribute, and an empty tile URL
   leaves the map box blank. Theme settings still win once they exist.

   OpenStreetMap's own tiles need no key, so the map is never broken out of
   the box. Their usage policy is aimed at low volume, so a storefront that
   grows should move to a licensed provider via the Map theme settings. */
const EVENT_MAP_TILE_FALLBACK = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  subdomains: 'abc',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
};

/* Whole-country view, used before the pins are known and when none place. */
const EVENT_MAP_US_VIEW = { center: [39.8283, -98.5795], zoom: 3 };

const EVENT_MAP_STATE_CENTROIDS = {
  AL: [32.7794, -86.8287],
  AZ: [34.2744, -111.6602],
  AR: [34.8938, -92.4426],
  CA: [37.1841, -119.4696],
  CO: [38.9972, -105.5478],
  CT: [41.6219, -72.7273],
  DE: [38.9896, -75.505],
  DC: [38.9101, -77.0147],
  FL: [28.6305, -82.4497],
  GA: [32.6415, -83.4426],
  ID: [44.3509, -114.613],
  IL: [40.0417, -89.1965],
  IN: [39.8942, -86.2816],
  IA: [42.0751, -93.496],
  KS: [38.4937, -98.3804],
  KY: [37.5347, -85.3021],
  LA: [31.0689, -91.9968],
  ME: [45.3695, -69.2428],
  MD: [39.055, -76.7909],
  MA: [42.2596, -71.8083],
  MI: [44.3467, -85.4102],
  MN: [46.2807, -94.3053],
  MS: [32.7364, -89.6678],
  MO: [38.3566, -92.458],
  MT: [47.0527, -109.6333],
  NE: [41.5378, -99.7951],
  NV: [39.3289, -116.6312],
  NH: [43.6805, -71.5811],
  NJ: [40.1072, -74.6652],
  NM: [34.4071, -106.1126],
  NY: [42.9538, -75.5268],
  NC: [35.5557, -79.3877],
  ND: [47.4501, -100.4659],
  OH: [40.2862, -82.7937],
  OK: [35.5889, -97.4943],
  OR: [43.9336, -120.5583],
  PA: [40.8781, -77.7996],
  RI: [41.6762, -71.5562],
  SC: [33.9169, -80.8964],
  SD: [44.4443, -100.2263],
  TN: [35.858, -86.3505],
  TX: [31.4757, -99.3312],
  UT: [39.3055, -111.6703],
  VT: [44.0687, -72.6658],
  VA: [37.5215, -78.8537],
  WA: [47.3826, -120.4472],
  WV: [38.6409, -80.6227],
  WI: [44.6243, -89.9941],
  WY: [42.9957, -107.5512]
};

if (!customElements.get('event-map')) {
  customElements.define(
    'event-map',
    class EventMap extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.event-map');
        this.items = Array.from(this.querySelectorAll('[data-event-map-item]'));
        this.countEl = this.querySelector('[data-event-map-count]');
        this.emptyEl = this.querySelector('[data-event-map-empty]');
        this.form = this.querySelector('[data-event-map-search]');
        this.input = this.querySelector('[data-event-map-query]');
        this.mapEl = this.querySelector('[data-event-map-leaflet]');

        this.query = '';
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* Selecting a row recentres the map, but the first paint should show
           every pin at once — the state map's whole-country view. */
        this.ready = false;

        this.bindJump();
        this.bindSearch();
        this.bindSelection();
        this.buildMap();
        this.bindZoom();
        this.apply();
        this.fitAll();

        this.ready = true;
      }

      /* "Past events" jump ------------------------------------- */

      /* Shopify generates the target section's wrapper id, so it cannot be
         written in Liquid. Match on the template section key suffix. */
      bindJump() {
        const link = this.section.querySelector('[data-event-map-jump]');
        if (!link) return;

        const key = link.dataset.eventMapJump;
        const target =
          document.getElementById('shopify-section-' + key) ||
          document.querySelector('[id$="__' + key + '"]');

        if (!target) {
          link.hidden = true;
          return;
        }

        if (!target.id) target.id = 'section-' + key;
        link.setAttribute('href', '#' + target.id);
      }

      /* Search -------------------------------------------------- */

      bindSearch() {
        if (!this.form) return;

        this.form.addEventListener('submit', (event) => event.preventDefault());

        let timer;
        this.input.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            this.query = this.input.value.trim().toLowerCase();
            this.apply();
          }, 150);
        });
      }

      /* Zoom ---------------------------------------------------- */

      bindZoom() {
        if (!this.map) return;

        this.section.querySelectorAll('[data-event-map-zoom]').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.dataset.eventMapZoom === 'in') this.map.zoomIn();
            else this.map.zoomOut();
          });
        });
      }

      /* Selection ----------------------------------------------- */

      bindSelection() {
        this.addEventListener('change', (event) => {
          if (event.target.classList.contains('event-map__select')) this.syncActive();
        });
      }

      /* Block ids contain hyphens, so read the id off the item the checked
         radio precedes rather than parsing it out of the radio's own id. */
      selectedId() {
        const radio = this.querySelector('.event-map__select:checked');
        const item = radio && radio.nextElementSibling;
        return item ? item.dataset.eventId : null;
      }

      /* A filter can hide the row that was expanded, leaving a result list
         with nothing open. Move the selection to the first visible row. */
      ensureSelection() {
        const checked = this.querySelector('.event-map__select:checked');
        if (checked && !checked.hidden) return;

        const first = Array.from(this.querySelectorAll('.event-map__select')).find(
          (radio) => !radio.hidden
        );
        if (first) first.checked = true;
      }

      syncActive() {
        const id = this.selectedId();
        let active = null;

        this.pins.forEach((pin) => {
          const on = pin.dataset.eventId === id;
          pin.classList.toggle('is-active', on);
          pin.setAttribute('aria-pressed', String(on));
          if (on) active = pin;
        });

        if (this.ready && this.map && active && !active.hidden) {
          this.map.setView(active.latLng, Math.max(this.map.getZoom(), this.defaultZoom), {
            animate: !this.reduced
          });
        }
      }

      /* Map ----------------------------------------------------- */

      buildMap() {
        this.pins = [];
        if (!this.mapEl) return;

        if (typeof L === 'undefined') {
          console.warn('event-map: leaflet.js did not load, the map cannot render.');
          return;
        }

        const zoomable = this.dataset.mapZoom === 'true';
        this.defaultZoom = parseInt(this.dataset.mapDefaultZoom, 10) || 6;

        this.map = L.map(this.mapEl, {
          zoomControl: false,
          /* The page scrolls past a full-height map; hijacking the wheel
             would trap the reader inside it. */
          scrollWheelZoom: false,
          dragging: zoomable,
          touchZoom: zoomable,
          doubleClickZoom: zoomable,
          boxZoom: zoomable,
          keyboard: false,
          zoomAnimation: !this.reduced,
          fadeAnimation: !this.reduced,
          markerZoomAnimation: !this.reduced
        });

        /* Leaflet defers addLayer through whenReady until the map has a view,
           so a marker added before this never gets its icon element. fitAll()
           replaces this view once the search filter has run. */
        this.map.setView(EVENT_MAP_US_VIEW.center, EVENT_MAP_US_VIEW.zoom);

        /* Default is bottom-right, which is where the zoom buttons sit. The
           "Leaflet" prefix is a courtesy credit, not a licence condition, so
           it goes; the tile provider's own credit has to stay. */
        this.map.attributionControl.setPosition('bottomleft').setPrefix(false);

        L.tileLayer(this.dataset.tileUrl || EVENT_MAP_TILE_FALLBACK.url, {
          attribution: this.dataset.tileAttribution || EVENT_MAP_TILE_FALLBACK.attribution,
          subdomains: this.dataset.tileSubdomains || EVENT_MAP_TILE_FALLBACK.subdomains,
          maxZoom: parseInt(this.dataset.tileMaxZoom, 10) || 19,
          /* Leaflet substitutes any layer option named in the URL template,
             so {apikey} works whatever the provider calls its parameter.
             Must be a string — an undefined placeholder makes it throw. */
          apikey: this.dataset.tileApiKey || ''
        }).addTo(this.map);

        this.buildPins();
      }

      /* Pins ---------------------------------------------------- */

      /* The pin stays the same <button> the CSS and the row/pin sync already
         expect. Leaflet only supplies its position: a zero-size,
         non-interactive divIcon wrapper sits at the coordinate, and the
         button translates itself so the point of its icon lands there. */
      buildPins() {
        const label = this.dataset.selectLabel || '';
        const iconTemplate = this.querySelector('[data-pin-icon-template]');

        this.items.forEach((item) => {
          const at = this.locate(item);
          if (!at) return;

          const pin = document.createElement('button');
          pin.type = 'button';
          pin.className = 'event-map__pin';
          pin.dataset.eventId = item.dataset.eventId;
          pin.latLng = at;

          const name = item.querySelector('.event-map__row-name');
          pin.setAttribute('aria-label', label.replace('[title]', name ? name.textContent : ''));
          pin.setAttribute('aria-pressed', 'false');

          /* The pin shape is inline SVG, cloned from the template the section
             renders once — a CSS-only shape cannot taper from the head into
             the point, which is what made the old pins read as a balloon. */
          if (iconTemplate) {
            pin.appendChild(iconTemplate.content.cloneNode(true));
          }

          pin.addEventListener('click', () => {
            const radio = item.previousElementSibling;
            if (!radio || !radio.classList.contains('event-map__select')) return;
            radio.checked = true;
            this.syncActive();
            item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });

          L.marker(at, {
            icon: L.divIcon({ className: 'event-map__marker', html: '', iconSize: [0, 0] }),
            interactive: false,
            keyboard: false
          })
            .addTo(this.map)
            .getElement()
            .appendChild(pin);

          this.pins.push(pin);
        });
      }

      /* An explicit lat/lng on the block wins; otherwise the centre of its
         state, which is where every pin sat before coordinates existed. */
      locate(item) {
        const lat = parseFloat(item.dataset.lat);
        const lng = parseFloat(item.dataset.lng);
        if (!isNaN(lat) && !isNaN(lng)) return [lat, lng];

        return EVENT_MAP_STATE_CENTROIDS[item.dataset.eventState] || null;
      }

      /* Frame every visible pin. One pin has no extent to fit, so it gets
         the default zoom instead. */
      fitAll() {
        if (!this.map) return;

        const points = this.pins.filter((pin) => !pin.hidden).map((pin) => pin.latLng);

        if (points.length > 1) {
          this.map.fitBounds(points, { padding: [32, 32], animate: false });
        } else if (points.length === 1) {
          this.map.setView(points[0], this.defaultZoom, { animate: false });
        } else {
          this.map.setView(EVENT_MAP_US_VIEW.center, EVENT_MAP_US_VIEW.zoom, { animate: false });
        }
      }

      /* Apply the search filter ---------------------------------- */

      apply() {
        let shown = 0;

        this.items.forEach((item) => {
          const visible = !this.query || (item.dataset.search || '').indexOf(this.query) !== -1;

          item.hidden = !visible;
          if (item.previousElementSibling) item.previousElementSibling.hidden = !visible;
          if (visible) shown += 1;
        });

        this.pins.forEach((pin) => {
          const owner = this.items.find((item) => item.dataset.eventId === pin.dataset.eventId);
          pin.hidden = !owner || owner.hidden;
        });

        if (this.emptyEl) this.emptyEl.hidden = shown > 0;

        this.ensureSelection();

        if (this.countEl) {
          const template = this.dataset.countUpcoming || '';
          this.countEl.textContent = template.replace('[count]', shown);
        }

        this.syncActive();
      }
    }
  );
}
